// Widget ↔ host postMessage bridge — the pure half (no React, no network).
//
// A dashboard widget is agent-authored HTML rendered in `<iframe
// sandbox="allow-scripts" srcdoc=…>` — NO allow-same-origin, so the frame has
// an opaque origin: no cookies, no console DOM, no authenticated fetch. Its
// ONLY capability is postMessage to the host page, which proxies allowlisted
// tool calls to the existing MCP-spec endpoint
// (`POST /v1/mcp-connections/:id/tools/:name/call`) as the VIEWING USER — a
// widget can never do more than the person looking at it.
import type { DashboardCallableTool, DashboardToolGrant } from '@cortex/types';

/** Envelope a widget posts up to the host. */
export interface IWidgetRequest {
  source: 'cortex-widget';
  type: 'tool-call' | 'list-tools';
  requestId: string;
  mcpConnectionId?: string;
  toolName?: string;
  args?: Record<string, unknown>;
}

/** Envelope the host posts back down to the widget. */
export interface IHostReply {
  source: 'cortex-host';
  requestId: string;
  ok: boolean;
  data?: unknown;
  error?: string;
}

/** Validate an inbound message into a typed request; null = not ours, ignore. */
export function parseWidgetRequest(data: unknown): IWidgetRequest | null {
  if (!data || typeof data !== 'object') return null;
  const msg = data as Record<string, unknown>;
  if (msg.source !== 'cortex-widget') return null;
  if (msg.type !== 'tool-call' && msg.type !== 'list-tools') return null;
  if (typeof msg.requestId !== 'string' || !msg.requestId) return null;
  if (msg.type === 'tool-call') {
    if (typeof msg.mcpConnectionId !== 'string' || !msg.mcpConnectionId) return null;
    if (typeof msg.toolName !== 'string' || !msg.toolName) return null;
    if (msg.args !== undefined && (typeof msg.args !== 'object' || msg.args === null)) return null;
  }
  return msg as unknown as IWidgetRequest;
}

/**
 * A call is allowed only when it's in the widget's own allowlist AND still in
 * the org's currently-annotated callable set — so revoking an annotation
 * severs dependent widgets immediately, mirroring peer-grant re-checks.
 */
export function isToolAllowed(
  req: { mcpConnectionId?: string; toolName?: string },
  allowlist: DashboardToolGrant[],
  callable: DashboardCallableTool[],
): boolean {
  const granted = allowlist.some(
    (g) => g.mcpConnectionId === req.mcpConnectionId && g.toolName === req.toolName,
  );
  if (!granted) return false;
  return callable.some(
    (t) => t.mcpConnectionId === req.mcpConnectionId && t.toolName === req.toolName,
  );
}

/**
 * Unwrap an MCP CallToolResult into plain data for the widget: prefer
 * `structuredContent`, else the first text item (JSON-parsed when possible),
 * else the raw result. `isError` becomes an error string.
 */
export function unwrapCallToolResult(result: unknown): {
  ok: boolean;
  data?: unknown;
  error?: string;
} {
  if (!result || typeof result !== 'object') return { ok: true, data: result };
  const r = result as {
    isError?: boolean;
    structuredContent?: unknown;
    content?: Array<{ type?: string; text?: string }>;
  };
  const firstText = Array.isArray(r.content)
    ? r.content.find((c) => typeof c?.text === 'string')?.text
    : undefined;
  if (r.isError) return { ok: false, error: firstText ?? 'tool call failed' };
  if (r.structuredContent !== undefined) return { ok: true, data: r.structuredContent };
  if (firstText !== undefined) {
    try {
      return { ok: true, data: JSON.parse(firstText) };
    } catch {
      return { ok: true, data: firstText };
    }
  }
  return { ok: true, data: result };
}

/**
 * CSP for the widget document. Deliberate product call: widgets can load
 * external libraries (script-src https:, e.g. Three.js off a CDN), media
 * (img-src/media-src https:), and fetch/XHR to https: hosts (connect-src
 * https:, needed for loaders that fetch assets — glTF, textures, JSON).
 * This is a full network surface for agent-authored HTML, not a locked-down
 * one — the remaining guards are the sandbox's opaque origin (no cookies,
 * no same-origin credentialed requests) and style-src staying inline-only.
 */
const widgetCsp =
  "default-src 'none'; script-src 'unsafe-inline' https:; style-src 'unsafe-inline'; " +
  'img-src data: blob: https:; media-src data: blob: https:; font-src data: https:; ' +
  'connect-src https:';

/**
 * The widget-side API, injected ahead of the agent's HTML:
 *   window.cortex.callTool({mcpConnectionId, toolName, args}) → Promise<data>
 *   window.cortex.listTools() → Promise<DashboardCallableTool[]>
 */
const widgetShimScript = `(() => {
  let seq = 0;
  const prefix = Math.random().toString(36).slice(2);
  const pending = new Map();
  window.addEventListener('message', (e) => {
    const m = e.data;
    if (!m || m.source !== 'cortex-host' || typeof m.requestId !== 'string') return;
    const p = pending.get(m.requestId);
    if (!p) return;
    pending.delete(m.requestId);
    clearTimeout(p.timer);
    if (m.ok) p.resolve(m.data);
    else p.reject(new Error(m.error || 'bridge call failed'));
  });
  const post = (msg) =>
    new Promise((resolve, reject) => {
      const requestId = prefix + ':' + ++seq;
      const timer = setTimeout(() => {
        pending.delete(requestId);
        reject(new Error('bridge call timed out'));
      }, 30000);
      pending.set(requestId, { resolve, reject, timer });
      window.parent.postMessage({ source: 'cortex-widget', requestId, ...msg }, '*');
    });
  window.cortex = {
    callTool: ({ mcpConnectionId, toolName, args }) =>
      post({ type: 'tool-call', mcpConnectionId, toolName, args }),
    listTools: () => post({ type: 'list-tools' }),
  };
})();`;

/** Compose the srcdoc: CSP + bridge shim first, then the agent's HTML as-is. */
export function buildWidgetSrcdoc(html: string): string {
  return (
    '<!doctype html>' +
    `<meta http-equiv="Content-Security-Policy" content="${widgetCsp}">` +
    `<script>${widgetShimScript}</script>` +
    html
  );
}
